import type { CatalogMethod } from "./catalog-required-fields";

export type ValidationMode = "draft" | "publish";

export interface CatalogFormInput {
  biblio: {
    title?: string;
    authors?: string[];
    editors?: string[];
    isbn?: string;
    issn?: string;
    publisher?: string;
    placeOfPublication?: string;
    publishedDate?: string;
    callNumber?: string;
    bindingType?: string;
    subjects?: string[];
    type?: string;
    websiteUrl?: string;
  };
  journal?: {
    volumeNumber?: string;
    volumePublicationYear?: string;
    issueNumberFrom?: number;
    issueNumberTo?: number;
    issuePublicationYear?: string;
    issuePublicationDate?: string;
    volumeOnly?: boolean;
  };
  journalHoldings?: Array<{
    volumeNumber?: string;
    copies?: number;
    itemCopies?: Array<{
      accessionNumber?: number;
      acquisitionMode?: string;
      branchId?: string;
    }>;
  }>;
  copies: number;
  itemDefaults: {
    itemTypeId?: string;
    locationId?: string;
    branchId?: string;
    conditionCode?: string;
    statusCode?: string;
    acquisitionMode?: string;
    accessionNumbers?: number[];
  };
  itemCopies?: Array<{
    accessionNumber?: number;
    acquisitionMode?: string;
    branchId?: string;
  }>;
}

interface FieldError {
  field: string;
  message: string;
}

function hasText(value?: string | null): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function hasSubjects(subjects?: string[]): boolean {
  return (subjects ?? []).some((s) => hasText(s));
}

function push(errors: FieldError[], field: string, message: string): void {
  errors.push({ field, message });
}

/** Mirrors backend validateCatalogCreate; returns flat field keys for form error state. */
export function validateCatalogForm(
  data: CatalogFormInput,
  options: {
    method: CatalogMethod;
    mode: ValidationMode;
    accessionNumberMode: "AUTO" | "MANUAL";
    skipItemValidation?: boolean;
    /** Require a branch on each copy (MANUAL/ISBN). Set when the tenant has branches. */
    requireBranch?: boolean;
  }
): Record<string, string> {
  const errors: FieldError[] = [];
  const { biblio, journal, journalHoldings, copies, itemDefaults, itemCopies } = data;
  const { method, mode, accessionNumberMode, skipItemValidation, requireBranch } = options;
  const isManualAccession = accessionNumberMode === "MANUAL";
  const isPhysical = method !== "E_RESOURCE" && method !== "BULK";
  const isJournal = method === "JOURNAL";
  const holdings =
    journalHoldings && journalHoldings.length > 0
      ? journalHoldings
      : journal
        ? [{ volumeNumber: journal.volumeNumber, copies, itemCopies }]
        : [];
  const totalCopies = isJournal
    ? holdings.reduce((sum, holding) => sum + (holding.copies ?? 0), 0)
    : copies;

  if (!hasText(biblio.title)) {
    push(errors, "biblio.title", "Title is required");
  }

  if (mode === "draft") {
    return toFormErrors(errors);
  }

  if (isPhysical) {
    if (totalCopies < 1) {
      push(errors, "copies", "At least one copy is required");
    }
    if (!isJournal && (!biblio.authors?.length || !biblio.authors.some((a) => hasText(a)))) {
      push(errors, "biblio.authors", "At least one author is required");
    }
    if (isJournal) {
      if (!hasText(biblio.issn)) {
        push(errors, "biblio.issn", "ISSN is required");
      }
      if (holdings.length === 0) {
        push(errors, "journal.volumeNumber", "At least one volume is required");
      }
      holdings.forEach((holding, index) => {
        if (!hasText(holding.volumeNumber)) {
          push(errors, `journalHoldings.${index}.volumeNumber`, "Volume number is required");
          if (index === 0) {
            push(errors, "journal.volumeNumber", "Volume number is required");
          }
        }
        if (!holding.copies || holding.copies < 1) {
          push(errors, `journalHoldings.${index}.copies`, "At least one copy is required");
        }
      });
      if (!biblio.editors?.length || !biblio.editors.some((e) => hasText(e))) {
        push(errors, "biblio.editors", "At least one editor is required");
      }
      const websiteUrl = biblio.websiteUrl?.trim() ?? "";
      if (websiteUrl && !/^https?:\/\/.+/i.test(websiteUrl)) {
        push(errors, "biblio.websiteUrl", "Enter a valid http(s) URL");
      }
    }
    if ((method === "MANUAL" || method === "ISBN") && !hasText(biblio.isbn)) {
      push(errors, "biblio.isbn", "ISBN is required");
    }
    if (method === "MANUAL" || method === "ISBN") {
      if (!hasText(biblio.publisher)) push(errors, "biblio.publisher", "Publisher is required");
      if (!hasText(biblio.placeOfPublication)) {
        push(errors, "biblio.placeOfPublication", "Place of publication is required");
      }
      if (!hasText(biblio.publishedDate)) {
        push(errors, "biblio.publishedDate", "Publication date is required");
      }
      if (!hasText(biblio.callNumber)) push(errors, "biblio.callNumber", "Call number is required");
      if (!hasText(biblio.bindingType)) {
        push(errors, "biblio.bindingType", "Binding type is required");
      }
    }
    if (!hasSubjects(biblio.subjects)) {
      push(errors, "biblio.subjects", "At least one subject is required");
    }

    if (!skipItemValidation) {
      if (!hasText(itemDefaults.itemTypeId)) {
        push(errors, "itemDefaults.itemTypeId", "Item type is required");
      }
      if (!hasText(itemDefaults.locationId)) {
        push(errors, "itemDefaults.locationId", "Location is required");
      }
      if (!hasText(itemDefaults.conditionCode)) {
        push(errors, "itemDefaults.conditionCode", "Condition is required");
      }
      if (!hasText(itemDefaults.statusCode)) {
        push(errors, "itemDefaults.statusCode", "Status is required");
      }

      if (requireBranch && (method === "MANUAL" || method === "ISBN")) {
        for (let i = 0; i < copies; i++) {
          const branchId = itemCopies?.[i]?.branchId ?? itemDefaults.branchId;
          if (!hasText(branchId)) {
            push(errors, `itemCopies[${i}].branchId`, "Branch is required");
          }
        }
      }

      if (isJournal && journalHoldings && journalHoldings.length > 0) {
        let accessionOffset = 0;
        journalHoldings.forEach((holding, holdingIndex) => {
          const holdingCopies = holding.copies ?? 0;
          for (let i = 0; i < holdingCopies; i++) {
            const copy = holding.itemCopies?.[i];
            const acquisitionMode = copy?.acquisitionMode ?? itemDefaults.acquisitionMode;
            if (!hasText(acquisitionMode)) {
              push(
                errors,
                `journalHoldings.${holdingIndex}.itemCopies[${i}].acquisitionMode`,
                "Acquisition mode is required for each copy"
              );
            }
            if (isManualAccession) {
              const accession =
                copy?.accessionNumber ?? itemDefaults.accessionNumbers?.[accessionOffset + i];
              if (!accession || accession <= 0) {
                push(
                  errors,
                  `journalHoldings.${holdingIndex}.itemCopies[${i}].accessionNumber`,
                  "Accession numbers are required for all copies in manual mode"
                );
              }
            }
          }
          accessionOffset += holdingCopies;
        });
      } else {
        for (let i = 0; i < copies; i++) {
          const copy = itemCopies?.[i];
          const acquisitionMode = copy?.acquisitionMode ?? itemDefaults.acquisitionMode;
          if (!hasText(acquisitionMode)) {
            push(
              errors,
              `itemCopies[${i}].acquisitionMode`,
              "Acquisition mode is required for each copy"
            );
          }
          if (isManualAccession) {
            const accession = copy?.accessionNumber ?? itemDefaults.accessionNumbers?.[i];
            if (!accession || accession <= 0) {
              push(
                errors,
                `itemCopies[${i}].accessionNumber`,
                "Accession numbers are required for all copies in manual mode"
              );
            }
          }
        }
      }
    }
  }

  if (method === "E_RESOURCE") {
    if (!hasText(biblio.type)) {
      push(errors, "biblio.type", "Resource type is required");
    }
    if (!hasSubjects(biblio.subjects)) {
      push(errors, "biblio.subjects", "At least one subject is required");
    }
  }

  return toFormErrors(errors);
}

export function validateDigitalResourceForm(data: {
  title?: string;
  type?: string;
  subjects?: string[];
  hasFile: boolean;
  websiteUrl?: string;
  mode?: ValidationMode;
}): Record<string, string> {
  const mode = data.mode ?? "publish";
  const errors: Record<string, string> = {};

  if (!hasText(data.title)) {
    errors.title = "Title is required";
  }

  if (mode === "draft") {
    return errors;
  }

  const url = data.websiteUrl?.trim() ?? "";
  const hasValidUrl = /^https?:\/\/.+/i.test(url);
  if (url && !hasValidUrl) {
    errors.websiteUrl = "Enter a valid http(s) URL";
  }
  if (!data.hasFile && !hasValidUrl) {
    errors.file = "Upload a file or provide a website URL";
  }
  if (!hasText(data.type)) {
    errors.type = "Resource type is required";
  }
  if (!hasSubjects(data.subjects)) {
    errors.subjects = "At least one subject is required";
  }

  return errors;
}

export interface BulkImportRowInput {
  rowIndex: number;
  subjects?: string;
  acquisitionMode?: string;
  metadata?: { subjects?: string[] };
}

/** Client-side gate before bulk import confirm (subjects + acquisition mode per row). */
export function validateBulkImportRows(
  rows: BulkImportRowInput[],
  options?: { defaultAcquisitionMode?: string }
): string[] {
  const issues: string[] = [];
  const defaultAcquisitionMode = options?.defaultAcquisitionMode?.trim();

  for (const row of rows) {
    const subjectsFromField = row.subjects
      ?.split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const hasRowSubjects =
      (subjectsFromField?.length ?? 0) > 0 || (row.metadata?.subjects?.length ?? 0) > 0;

    if (!hasRowSubjects) {
      issues.push(`Row ${row.rowIndex}: at least one subject is required`);
    }
    const acquisitionMode = row.acquisitionMode?.trim() || defaultAcquisitionMode;
    if (!hasText(acquisitionMode)) {
      issues.push(`Row ${row.rowIndex}: acquisition mode is required`);
    }
  }

  return issues;
}

function toFormErrors(errors: FieldError[]): Record<string, string> {
  const fieldMap: Record<string, string> = {
    "biblio.title": "title",
    "biblio.authors": "authors",
    "biblio.editors": "editors",
    "biblio.issn": "issn",
    "biblio.isbn": "isbn",
    "biblio.publisher": "publisher",
    "biblio.placeOfPublication": "placeOfPublication",
    "biblio.publishedDate": "publishedDate",
    "biblio.callNumber": "callNumber",
    "biblio.bindingType": "bindingType",
    "biblio.subjects": "subjects",
    "biblio.type": "type",
    "biblio.websiteUrl": "websiteUrl",
    "journal.volumeNumber": "volumeNumber",
    "itemDefaults.itemTypeId": "itemTypeId",
    "itemDefaults.locationId": "locationId",
    "itemDefaults.conditionCode": "conditionCode",
    "itemDefaults.statusCode": "statusCode",
  };

  const result: Record<string, string> = {};

  for (const err of errors) {
    if (err.field.includes("acquisitionMode")) {
      result.acquisitionMode = err.message;
      if (err.field.startsWith("journalHoldings.")) {
        result[err.field] = err.message;
      }
      continue;
    }
    if (err.field.startsWith("journalHoldings.")) {
      result[err.field] = err.message;
      continue;
    }
    if (err.field.includes("branchId")) {
      result.branchId = err.message;
      continue;
    }
    if (err.field.includes("accessionNumber")) {
      result.accession = err.message;
      continue;
    }
    const key = fieldMap[err.field] ?? err.field;
    result[key] = err.message;
  }

  return result;
}
